import React from 'react';
import { Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar } from '@fortawesome/free-solid-svg-icons';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { NSFormGroup, NSInput, NSLabel } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { ExecutiveSummaryData } from '../../../types';
import { hasValidDecimalPlaces } from '../../../helpers';
import './ExecutiveSummaryForm.scss';
import { AI_ICON_URL } from '../../../constants';
import { useInvestmentMemoContext } from '../../../InvestmentMemoContext';

export interface ExecutiveSummaryFormProps {
    data: ExecutiveSummaryData;
    onChange: (data: Partial<ExecutiveSummaryData>) => void;
    isLoadingOpportunity?: boolean;
}

const ExecutiveSummaryForm = ({ data, onChange, isLoadingOpportunity = false }: ExecutiveSummaryFormProps) => {
    const { shouldShowError, getFieldError } = useInvestmentMemoContext();

    return (
        <div className="px-3 pb-3">
            <NSFormGroup>
                <NSLabel required>
                    Opportunity description
                    {' '}
                    <img
                        src={AI_ICON_URL}
                        className="NSInput__label-ai-icon rounded"
                        alt="Northspyre AI"
                    />
                </NSLabel>
                <RenderIf isTrue={isLoadingOpportunity}>
                    <div className="d-flex align-items-center justify-content-center p-2">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        <span className="text-muted">Generating opportunity description...</span>
                    </div>
                </RenderIf>
                <RenderIf isTrue={!isLoadingOpportunity}>
                    <NSInput
                        name="opportunityDescription"
                        type="textarea"
                        rows={4}
                        value={data.opportunityDescription}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange({ opportunityDescription: e.target.value })}
                        showErrorMsg={shouldShowError('executiveSummary.opportunityDescription')}
                        errorMsg={getFieldError('executiveSummary.opportunityDescription')}
                    />
                </RenderIf>
            </NSFormGroup>

            <div className="ExecutiveSummaryForm__key-metrics-container mt-3">
                <NSLabel>Key metrics</NSLabel>
                <Row>
                    <Col md={6}>
                        <NSFormGroup>
                            <NSInput
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                name="totalCapitalization"
                                type="number"
                                label="Total capitalization"
                                value={data.totalCapitalization}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (
                                    onChange({ totalCapitalization: String(Number(e.target.value) || '') })
                                )}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.totalCapitalization')}
                                errorMsg={getFieldError('executiveSummary.totalCapitalization')}
                            />
                        </NSFormGroup>
                    </Col>
                    <Col md={6}>
                        <NSFormGroup>
                            <NSInput
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                name="totalEquityRequired"
                                type="number"
                                label="Total equity required"
                                value={data.totalEquityRequired}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (
                                    onChange({ totalEquityRequired: String(Number(e.target.value) || '') })
                                )}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.totalEquityRequired')}
                                errorMsg={getFieldError('executiveSummary.totalEquityRequired')}
                            />
                        </NSFormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <NSFormGroup>
                            <NSInput
                                name="projectedLpIrr"
                                type="number"
                                label="Projected LP IRR"
                                value={data.projectedLpIrr}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ projectedLpIrr: String(Number(e.target.value) || '') })}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.projectedLpIrr')}
                                errorMsg={getFieldError('executiveSummary.projectedLpIrr')}
                                appendInputAddon={<span>%</span>}
                            />
                        </NSFormGroup>
                    </Col>
                    <Col md={6}>
                        <NSFormGroup>
                            <NSInput
                                name="projectedEquityMultiple"
                                type="number"
                                label="Projected equity multiple"
                                value={data.projectedEquityMultiple}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({
                                    projectedEquityMultiple: String(Number(e.target.value) || ''),
                                })}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.projectedEquityMultiple')}
                                errorMsg={getFieldError('executiveSummary.projectedEquityMultiple')}
                                appendInputAddon={<span>x</span>}
                            />
                        </NSFormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <NSFormGroup>
                            <NSInput
                                name="avgCashOnCash"
                                type="number"
                                label="Avg. cash-on-cash"
                                value={data.avgCashOnCash}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ avgCashOnCash: String(Number(e.target.value) || '') })}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.avgCashOnCash')}
                                errorMsg={getFieldError('executiveSummary.avgCashOnCash')}
                                appendInputAddon={<span>%</span>}
                            />
                        </NSFormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <NSFormGroup>
                            <NSInput
                                name="holdPeriod"
                                type="number"
                                label="Hold period"
                                value={data.holdPeriod}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const val = e.target.value;
                                    if (!hasValidDecimalPlaces(val, 2)) return;
                                    onChange({ holdPeriod: val });
                                }}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.holdPeriod')}
                                errorMsg={getFieldError('executiveSummary.holdPeriod')}
                                appendInputAddon={<span>years</span>}
                            />
                        </NSFormGroup>
                    </Col>
                    <Col md={6}>
                        <NSFormGroup>
                            <NSInput
                                name="buildPeriod"
                                type="number"
                                label="Build period"
                                value={data.buildPeriod}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const val = e.target.value;
                                    if (!hasValidDecimalPlaces(val, 2)) return;
                                    onChange({ buildPeriod: val });
                                }}
                                required
                                showErrorMsg={shouldShowError('executiveSummary.buildPeriod')}
                                errorMsg={getFieldError('executiveSummary.buildPeriod')}
                                appendInputAddon={<span>months</span>}
                            />
                        </NSFormGroup>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default ExecutiveSummaryForm;
